import React, { useState } from "react";
import moment from "moment";
import "moment/locale/fr";
import { IconButton } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import Functions from "../../Helpers/Functions";
import TimeFields from "../../Components/Fields/TimeFields";
import DurationFields from "../../Components/Fields/DurationFields";
import ButtonField from "../../Components/Ui/ButtonField";
import { BrandContent, useTailwindBrand } from "../../Components/shared";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { getActivePrestationBrandConfig } from "../../config/environment.config";
import {
    CalendarDefaults,
    WEEKDAYS,
    defaultStartTime,
    initialCalendarMonth,
    isSelectableDay,
    isWeekdayFullySelected,
    monthGrid,
    mostCommonDuration,
    mostCommonStartTime,
    rowsFromSelectedDates,
    selectedDatesFromRows,
    toggleDate,
    toggleWeekday,
} from "./sessionCalendarHelpers";
import { MAX_SESSIONS_MONTH, MIN_SESSIONS_MONTH, SessionRow } from "./SessionList";

interface Props {
    /** Lignes déjà saisies (vides pour une nouvelle déclaration). */
    rows: SessionRow[];
    /** Reçoit les lignes générées, puis l'appelant repasse en mode liste. */
    onGenerate: (rows: SessionRow[]) => void;
    /** Retour à la liste sans rien changer (quand une liste existe déjà). */
    onCancel?: () => void;
    /** Premier jour sélectionnable (validation du compte d'avance immédiate ou début du mois en cours). */
    minDate?: string | null;
    startAt?: any;
}

/**
 * Calendrier du mois de la déclaration « Plusieurs cours » : le prof coche
 * ses jours, un raccourci par jour de semaine coche tous les mardis d'un
 * coup, puis l'heure et la durée par défaut s'appliquent aux dates cochées.
 * La liste des séances reste modifiable ensuite (SessionList).
 */
const SessionCalendar = ({ rows, onGenerate, onCancel, minDate, startAt }: Props) => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const labels = getActivePrestationBrandConfig("s");
    const [month, setMonth] = useState(() => initialCalendarMonth(rows, minDate));
    const [selected, setSelected] = useState<string[]>(() => selectedDatesFromRows(rows));
    // Valeurs par défaut à l'ouverture : l'heure et la durée les plus fréquentes
    // de la liste, sinon l'heure actuelle arrondie et 1 h 30. Si le prof les
    // modifie ici, elles s'appliquent à toutes les séances générées.
    const [initialDefaults] = useState<CalendarDefaults>(() => ({
        startTime: mostCommonStartTime(rows) ?? defaultStartTime(),
        duration: mostCommonDuration(rows),
    }));
    const [startTime, setStartTime] = useState<string | null>(initialDefaults.startTime);
    const [duration, setDuration] = useState<number>(initialDefaults.duration);
    const [limitReached, setLimitReached] = useState(false);

    // Navigation : du mois du premier jour sélectionnable au mois suivant le mois en cours
    const firstMonth = initialCalendarMonth([], minDate);
    const lastMonth = moment().add(1, "month").startOf("month");
    const canGoPrevious = month.isAfter(firstMonth, "month");
    const canGoNext = month.isBefore(lastMonth, "month");

    const applyToggle = (result: { dates: string[]; refused: boolean }) => {
        setSelected(result.dates);
        setLimitReached(result.refused);
    };

    const selectedInMonth = selected.filter((d) => moment(d).isSame(month, "month"));
    const otherMonthCount = selected.length - selectedInMonth.length;
    const canGenerate = selected.length >= MIN_SESSIONS_MONTH;

    const generate = () => {
        onGenerate(rowsFromSelectedDates(selected, rows, { startTime, duration }, initialDefaults));
    };

    const primary = brandConfig.colors.primary;

    return (
        <div className="w-full space-y-4">
            <BrandContent
                tag="p"
                children={<>Cochez les jours de vos {labels} du mois, ou un jour de la semaine pour tout cocher d'un coup. Vous pourrez ensuite ajuster chaque séance.</>}
                type="description"
                className={common.body}
            />

            {/* En-tête du mois */}
            <div className="flex items-center justify-between">
                <IconButton size="small" aria-label="Mois précédent" onClick={() => setMonth(month.clone().subtract(1, "month"))} disabled={!canGoPrevious}>
                    <ChevronLeftRoundedIcon />
                </IconButton>
                <BrandContent
                    tag="p"
                    children={<>{capitalizeFirstLetter(month.clone().locale("fr").format("MMMM YYYY"))}</>}
                    type="description"
                    className={`font-bold ${classes.primaryText}`}
                />
                <IconButton size="small" aria-label="Mois suivant" onClick={() => setMonth(month.clone().add(1, "month"))} disabled={!canGoNext}>
                    <ChevronRightRoundedIcon />
                </IconButton>
            </div>

            {/* Raccourcis par jour de semaine */}
            <div className="grid grid-cols-7 gap-1">
                {WEEKDAYS.map((weekday) => {
                    const active = isWeekdayFullySelected(selected, month, weekday.iso, minDate);
                    return (
                        <button
                            key={weekday.iso}
                            type="button"
                            aria-pressed={active}
                            aria-label={`Tous les ${weekday.long}s`}
                            title={`Tous les ${weekday.long}s du mois`}
                            onClick={() => applyToggle(toggleWeekday(selected, month, weekday.iso, MAX_SESSIONS_MONTH, minDate))}
                            className="rounded-md border py-1 text-xs font-semibold transition-colors"
                            style={{
                                borderColor: primary,
                                backgroundColor: active ? primary : "transparent",
                                color: active ? "#fff" : primary,
                                fontFamily: brandConfig.fontFamily,
                            }}
                        >
                            {weekday.short}
                        </button>
                    );
                })}
            </div>

            {/* Grille des jours */}
            <div className="grid grid-cols-7 gap-1" role="grid" aria-label={`Jours de ${month.clone().locale("fr").format("MMMM YYYY")}`}>
                {monthGrid(month).flat().map((date, index) => {
                    if (!date) {
                        return <div key={`empty-${index}`} aria-hidden="true" />;
                    }
                    const selectable = isSelectableDay(date, minDate);
                    const isSelected = selected.includes(date);
                    const isToday = moment(date).isSame(moment(), "day");
                    return (
                        <button
                            key={date}
                            type="button"
                            role="gridcell"
                            aria-selected={isSelected}
                            aria-label={capitalizeFirstLetter(moment(date).locale("fr").format("dddd D MMMM"))}
                            disabled={!selectable}
                            onClick={() => applyToggle(toggleDate(selected, date, MAX_SESSIONS_MONTH))}
                            className={`h-10 rounded-md border text-sm font-medium transition-colors ${selectable ? "" : "cursor-not-allowed opacity-30"}`}
                            style={{
                                borderColor: isSelected || isToday ? primary : "#e5e7eb",
                                backgroundColor: isSelected ? primary : "transparent",
                                color: isSelected ? "#fff" : "inherit",
                                fontFamily: brandConfig.fontFamily,
                            }}
                        >
                            {moment(date).date()}
                        </button>
                    );
                })}
            </div>

            <BrandContent
                tag="p"
                children={
                    <>
                        <strong>{selected.length}</strong> date{selected.length > 1 ? "s" : ""} cochée{selected.length > 1 ? "s" : ""} sur {MAX_SESSIONS_MONTH} maximum
                        {otherMonthCount > 0 && <> (dont {otherMonthCount} sur un autre mois)</>}
                        {limitReached && <span className="text-red-600"> : limite atteinte</span>}
                    </>
                }
                type="description"
                className={`italic ${common.body}`}
            />

            {/* Heure et durée par défaut */}
            <div className="lg:grid flex flex-col grid-cols-2 gap-3">
                <TimeFields
                    value={startTime ? new Date(startTime) : undefined}
                    date={selected[0] ? moment(selected[0]) : moment()}
                    setStartTime={(date: Date) => setStartTime(date.toISOString())}
                    setEndTime={() => undefined}
                    duration={duration}
                    startAt={startAt}
                    label="Heure de début des séances"
                />
                <DurationFields
                    customDuration={""}
                    value={Functions.renderDuration(duration * 2)}
                    isPunctualMonth={false}
                    duration={duration}
                    setDuration={(updater: any) => {
                        setDuration((current) => (typeof updater === "function" ? updater(current) : updater));
                    }}
                    setEndTime={() => undefined}
                    startTime={startTime ? new Date(startTime) : undefined}
                    label="Durée de chaque séance"
                />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                {onCancel ? (
                    <ButtonField label="Revenir à la liste" variant="text" onClick={onCancel} />
                ) : <span />}
                <ButtonField
                    label={`Générer les ${selected.length > 1 ? selected.length + " " : ""}séances`}
                    variant="contained"
                    onClick={generate}
                    disabled={!canGenerate}
                />
            </div>
            {!canGenerate && (
                <BrandContent
                    tag="p"
                    children={<>Cochez au moins {MIN_SESSIONS_MONTH} dates pour une déclaration « Plusieurs {labels} ».</>}
                    type="description"
                    className={`italic ${common.body}`}
                />
            )}
        </div>
    );
};

export default SessionCalendar;
